/**
 * PatientsFilters.tsx — the Patients directory's filter panel.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 6 §5 (PMS).
 *
 * WHY A PANEL FOR ONE FACET (2026-09-05). The directory used to carry a bare
 * inline checkbox under its title row while the CRM board offered the same idea
 * — the same words, even — behind a "Filters (n)" button, a modal and a
 * removable chip. Two staff screens with two different shapes for "narrow what
 * this list shows" is the defect; an admin who has learned the board's bar
 * should not have to learn a second grammar here.
 *
 * So the shape is the board's, and it is REUSED rather than re-implemented: the
 * dialog frame is the estate's `DialogShell` and every control inside comes
 * from `ui/filters.tsx`, the module the pipeline's own panel now draws from
 * too. One facet today is not an argument for a different shape — it is an
 * argument for the shared shape being cheap, and the next facet (verified,
 * in-treatment, registered-since) lands as one more `FilterTick` rather than as
 * a second redesign.
 *
 * ⚠️ THE DRAFT IS THE POINT, exactly as it is on the board. Applying on every
 * press would fire a server read per click — and on THIS screen a read is not
 * merely wasted: `showTest` resets the whole cursor-page chain (see
 * PatientsTab), so a mid-panel toggle would throw away the admin's place in the
 * directory before they had finished choosing. The panel edits a local copy and
 * Apply commits it once.
 */
import { useState } from 'react';
import { DialogShell } from '../ui/ConfirmDialog';
import { FilterFooter, FilterSection, FilterTick, filterNoteStyle } from '../ui/filters';

export interface PatientsFilterState {
  /** The opt-in audit view — sends identity's `includeTest=true`. */
  showTest: boolean;
}

export const EMPTY_PATIENT_FILTERS: PatientsFilterState = { showTest: false };

/** How many distinct choices a state represents — the bar's badge count. */
export function activePatientFilterCount(f: PatientsFilterState): number {
  return f.showTest ? 1 : 0;
}

/** The chip wording for an applied `showTest`, shared with the toolbar. */
export const TEST_RECORDS_CHIP = 'Test records shown';

export function PatientsFilters({
  applied,
  onApply,
  onClose,
}: {
  applied: PatientsFilterState;
  onApply: (next: PatientsFilterState) => void;
  onClose: () => void;
}) {
  // Seeded from what is applied, so opening shows the current state and
  // closing without applying changes nothing.
  const [draft, setDraft] = useState<PatientsFilterState>(applied);

  return (
    <DialogShell title="Filters" onCancel={onClose} width="min(720px, 100%)">
      {/* "Show test records" — THE PIPELINE'S LABEL, VERBATIM, and under the
          same "Other" heading. The same idea under two names on two staff
          screens is the defect this panel exists to close, so the words are
          copied deliberately rather than re-written to suit the directory.

          The HINT is the directory's own, though: the board's version ends on a
          clause about its stage totals excluding marked rows, and this screen
          has no totals to contradict. Repeating a sentence about numbers that
          are not on the page is how copied copy stops being read. */}
      <FilterSection label="Other" hint="Reporting hygiene, not patient work.">
        <FilterTick
          label="Show test records"
          describedBy="pms-filters-test-hint"
          checked={draft.showTest}
          onChange={() => setDraft((d) => ({ ...d, showTest: !d.showTest }))}
        />
        <p id="pms-filters-test-hint" style={{ ...filterNoteStyle, margin: '4px 0 0 30px' }}>
          Marked dry-run accounts are off the directory by default. Nothing is deleted &mdash; they
          stay here behind this switch.
        </p>
      </FilterSection>

      <FilterFooter onClear={() => setDraft(EMPTY_PATIENT_FILTERS)} onApply={() => onApply(draft)} />
    </DialogShell>
  );
}
